import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, ArrowDown } from 'lucide-react';
import { MaskedLine, Reveal } from '../components/Reveal';
import { site, img } from '../data/site';

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, -140]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-screen flex-col overflow-hidden bg-pine"
    >
      <motion.div
        style={{ y: yOrb }}
        aria-hidden="true"
        className="pointer-events-none absolute -right-48 top-[-12%] h-[640px] w-[640px] rounded-full bg-[radial-gradient(circle_at_center,rgba(168,213,186,0.25),rgba(6,75,43,0.28)_45%,transparent_70%)] blur-2xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 bottom-[-20%] h-[480px] w-[480px] rounded-full bg-emerald-600/50 blur-[130px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-[-10%] top-[28%] h-[540px] w-[540px] rounded-full bg-[radial-gradient(circle_at_center,rgba(217,70,239,0.22),rgba(139,92,246,0.13)_45%,transparent_70%)] blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-[8%] top-[16%] hidden h-40 w-40 animate-spin-slow rounded-full border border-dashed border-emerald-400/50 lg:block"
      />

      <div className="relative mx-auto flex w-full max-w-7xl flex-1 items-center px-5 pb-16 pt-14 sm:px-8 sm:pt-20">
        <div className="grid w-full items-center gap-14 lg:grid-cols-[1.12fr_0.88fr]">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mb-8 inline-flex items-center gap-3 rounded-full border border-goldlight/40 bg-white/5 px-5 py-2 backdrop-blur-sm"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-goldlight" aria-hidden="true" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-goldlight sm:text-xs">
                {site.tagline}
              </span>
            </motion.div>

            <h1 className="text-balance font-display text-[2.65rem] font-bold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
              <MaskedLine delay={0.3}>Your Business Deserves</MaskedLine>
              <MaskedLine delay={0.42}>More Than Just</MaskedLine>
              <MaskedLine delay={0.54}>
                <em className="text-rainbow">Being Online.</em>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-white sm:text-lg"
            >
              We help businesses get discovered, attract the right audience, and turn digital
              visibility into real opportunities through strategic marketing, creative content,
              technology, and digital expertise.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-mint">
                Let's Grow Your Business <ArrowUpRight size={17} />
              </Link>
              <Link to="/services" data-testid="hero-cta-secondary" className="btn-outline-light">
                Explore Our Services
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.15 }}
              className="mt-10 grid max-w-xl grid-cols-3 gap-3"
              data-testid="hero-stat-chips"
            >
              {[
                { n: '08', l: 'Core Services' },
                { n: '01', l: 'Growth Partner' },
                { n: '100%', l: 'Virtual Trainings' },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl border-2 border-white bg-white px-4 py-4 text-center">
                  <p className="font-display text-2xl font-bold text-forest sm:text-3xl">{s.n}</p>
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-ink">
                    {s.l}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            <motion.div style={{ y: yImg }} className="relative">
              <div
                className="absolute -left-5 -top-5 h-full w-full rounded-[1.75rem] border border-goldlight/40"
                aria-hidden="true"
              />
              <div className="img-frame aspect-[4/5] rounded-[1.75rem] shadow-[0_50px_120px_-40px_rgba(0,0,0,0.75)]">
                <img
                  src={img('photo-1573164713988-8665fc963095', 1000)}
                  alt="Business owner growing her brand from a modern digital workspace"
                />
              </div>
              <div className="absolute -bottom-7 -left-10 animate-float rounded-2xl border border-white/10 bg-white/10 px-6 py-4 backdrop-blur-xl">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-goldlight">
                  One Hub. Many Solutions.
                </p>
                <p className="mt-1 font-display text-xl font-bold text-white">Maximum Impact.</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <Reveal delay={1.15} y={10} className="relative border-t border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-white sm:text-xs">
            8 Core Services · One Dedicated Growth Partner
          </p>
          <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em] text-goldlight sm:text-xs">
            Scroll <ArrowDown size={14} className="animate-bounce" />
          </span>
        </div>
      </Reveal>
    </section>
  );
};
